import type {
    AdministrationLegacyRoute,
    AdministrationLocation,
} from "@kontextwerk/control-web-frame/shared/logic/administration"
import type { OrgaViewFrameContext } from "../context"

/** Historical tab ordering is frozen independently of the new catalog. */
export const getLegacyAdministrationRoutes = (
  context: OrgaViewFrameContext,
  location?: AdministrationLocation,
): AdministrationLegacyRoute[] => {
  const allows = (
    resource: string,
    level: NonNullable<Parameters<OrgaViewFrameContext["permissions"]["allows"]>[1]>,
  ) => context.permissions.allows(resource, level)
  const settings = allows("core.org_settings", "view")
  const restricted = context.subscriptionRestrictionActive
  const billing =
    context.billingAvailable &&
    context.billingEnabled &&
    allows("core.billing", "view") &&
    (context.billingRequiresCheckout ||
      context.tariffCapabilities.hasUsage ||
      allows("core.billing", "manage"))
  const danger = Boolean(
    context.organization &&
    allows("core.org_settings", "manage") &&
    context.currentUserOrganizationId === context.organization.id,
  )
  const documents = context.billingAvailable && allows("core.billing", "view")
  const session =
    context.tariffCapabilities.hasSessionConfig && allows("core.assistant_configuration", "view")
  const signature = new URLSearchParams(location?.search ?? "").get("signature_action") === "draw"
  const planningTarget = signature
    ? "administration/announcement-templates/letter/other"
    : "administration/planning"
  const managementTargets = [
    ...(settings ? ["administration/order-types"] : []),
    ...(!restricted && allows("core.memberships", "view") ? ["administration/users/members"] : []),
    ...(documents ? ["administration/legal-documents"] : []),
    ...(!restricted && context.tariffCapabilities.hasAiAgents && allows("core.projects", "view")
      ? ["administration/agents"]
      : []),
    ...(allows("core.communication_channels", "view") && context.tariffCapabilities.hasPhone
      ? ["administration/phone-numbers"]
      : []),
    ...(!restricted ? ["feedback"] : []),
  ]
  const settingsTargets = [
    ...(settings ? ["administration/organization/general", planningTarget] : []),
    ...(billing ? ["administration/billing"] : []),
    ...(allows("core.audit_log", "view") ? ["administration/audit"] : []),
    ...(danger ? ["administration/organization/danger"] : []),
  ]

  return [
    { path: "management", targets: managementTargets, defaultTarget: "administration" },
    { path: "organization", targets: settingsTargets },
    {
      path: "session-details",
      targets: session
        ? ["administration/ai-rules/answer-rules", "administration/ai-rules/human-handover"]
        : [],
    },
    { path: "customers", targets: ["administration/customers", "administration/properties"] },
    { path: "users", targets: ["administration/users/members"] },
    { path: "billing", targets: ["administration/billing"] },
    {
      path: "administration/organization",
      targets: [
        ...(settings ? ["administration/organization/general"] : []),
        ...(danger ? ["administration/organization/danger"] : []),
      ],
    },
    ...(signature ? [{ path: "administration/planning", targets: [planningTarget] }] : []),
  ]
}

export const legacyAdministrationAliases = {
  "administration/district/customers": "administration/customers",
  "administration/district/addresses": "administration/properties",
  // The interim V2 identity entries are folded back into the restored Benutzer area.
  "administration/memberships": "administration/users/members",
  "administration/roles": "administration/users/roles",
  "administration/invitations": "administration/users/members",
  "administration/membership-settings": "administration/users/members",
}
